import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons'
import { useReselector } from '@/utils/sharedHooks'
import { useYearEndNextStepPill } from '@/features/year-end-module-status/components/EOYNextStepPill'
import { selectYearEndModuleByType } from '@/features/year-end-module-status/yearEndModuleStatus.selectors'
import {
  YearEndModuleType,
  YearEndModuleStatusOptions,
} from '@/features/year-end-module-status/yearEndModuleStatus.slice'
import {
  selectCurrentAnnualTaxDetails,
  selectCurrentAnnualTaxYear,
} from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import { getCurrentUser, getFinancialProfile } from '@/selectors/user.selectors'
import { getAnnualTaxFilingForYearSelector } from '@/features/taxes/annual-taxes/annualTaxFilings.selector'
import { TAX_ENTITY_TYPES } from '@/features/taxes/taxConstants'

import { TextColors, TypographyStyles } from '@/styles'
import { Badge, Button, Card, CardColors, Image, Text } from '@/ui'
import { convertUtcToLocalDate, DATE_FORMATS_LUXON } from '@/utils/dateHelpers'

export const TaxSeasonKickoffEntryCard = () => {
  const navigate = useNavigate()
  const currentUser = useReselector(getCurrentUser)
  const taxYear = useReselector(selectCurrentAnnualTaxYear)
  const annualTaxDetails = useReselector(selectCurrentAnnualTaxDetails)

  const annualTaxFiling = useReselector(
    getAnnualTaxFilingForYearSelector,
    taxYear
  )

  const financialProfile = useReselector(getFinancialProfile)
  const { pretaxSurveySubmittedAt, optedOutAt } = annualTaxFiling || {}
  const nextStepPill = useYearEndNextStepPill()

  // Check tax return completion status
  const form1040Module = useReselector(
    selectYearEndModuleByType,
    YearEndModuleType.file1040
  )
  const form1120sModule = useReselector(
    selectYearEndModuleByType,
    YearEndModuleType.file1120S
  )

  // Determine if all required tax returns are complete based on user's tax entity type
  const allRequiredTaxReturnsComplete = useMemo(() => {
    const taxEntityType = financialProfile?.taxEntityType

    // Determine which forms are required based on tax entity type
    const requiresForm1040 =
      taxEntityType === TAX_ENTITY_TYPES.form_1040 ||
      taxEntityType === TAX_ENTITY_TYPES.schedule_c ||
      taxEntityType === TAX_ENTITY_TYPES.form_1120_s
    const requiresForm1120s = taxEntityType === TAX_ENTITY_TYPES.form_1120_s

    // Check if required forms are complete
    const form1040Complete =
      !requiresForm1040 ||
      form1040Module?.status === YearEndModuleStatusOptions.complete
    const form1120sComplete =
      !requiresForm1120s ||
      form1120sModule?.status === YearEndModuleStatusOptions.complete

    return form1040Complete && form1120sComplete
  }, [
    financialProfile?.taxEntityType,
    form1040Module?.status,
    form1120sModule?.status,
  ])

  const cardState = useMemo(() => {
    if (pretaxSurveySubmittedAt) {
      const mostUrgentItemPill = <div>{nextStepPill}</div>

      return {
        headerText: 'File your annual tax return ',
        dueDate: convertUtcToLocalDate(
          annualTaxDetails?.form_1040_irs_due_date
        )?.toFormat(DATE_FORMATS_LUXON.MONTH_DAY),
        description: (
          <>
            Close out {taxYear} by getting everything in order for annual taxes.
          </>
        ),
        nextStepPill: mostUrgentItemPill,
        button: {
          text: 'Go to Annual Taxes',
          onClick: () => navigate('/taxes/annual'),
        },
      }
    }

    return {
      headerText: 'Tell us your tax needs',
      dueDate: 'ASAP',
      description: (
        <>
          Personalize your tax season, and we&apos;ll guide you through every
          step and deadline to keep you on track. We recommend completing this{' '}
          <b>ASAP</b>. It only takes 5 minutes!
        </>
      ),
      nextStepPill: null,
      button: {
        text: 'Get Started',
        onClick: () => navigate('/taxes/annual'),
      },
    }
  }, [
    annualTaxDetails?.form_1040_irs_due_date,
    navigate,
    nextStepPill,
    pretaxSurveySubmittedAt,
    taxYear,
  ])

  if (!currentUser || optedOutAt || allRequiredTaxReturnsComplete) {
    return null
  }

  return (
    <Card color={CardColors.YELLOW_GRADIENT}>
      <div className="flex justify-between items-center">
        <div className="flex flex-col w-3/4 gap-6">
          <div className="flex flex-col gap-2">
            <Text variant={TypographyStyles.EYEBROW} color={TextColors.MUTED}>
              {taxYear} Annual Taxes
            </Text>

            <div className="flex items-center gap-4">
              <Text variant={TypographyStyles.DISPLAY_MD}>
                {cardState.headerText}
              </Text>
              <Badge color="orange">Due {cardState.dueDate}</Badge>
            </div>

            <Text
              variant={TypographyStyles.BODY_SM}
              className="leading-relaxed w-[480px]"
            >
              {cardState.description}
            </Text>
          </div>

          {cardState.nextStepPill}

          {cardState.button && (
            <Button
              variant="primary"
              iconRight={faArrowRight}
              onClick={cardState.button.onClick}
              className="w-fit"
            >
              {cardState.button.text}
            </Button>
          )}
        </div>

        <div className="mr-14">
          <Image
            src="https://heard-images.s3.amazonaws.com/assets/annual_taxes_compass_v2.svg"
            alt="Annual taxes illustration"
            width={180}
          />
        </div>
      </div>
    </Card>
  )
}
